import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { buildApiUrl } from "./apiConfig";

const WAITINGLIST_ENDPOINT = buildApiUrl("/api/searchluxura/admin/waitinglist");
const POLL_INTERVAL_MS = 3000;

function WaitingListPage() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchWaitinglist = useCallback(async ({ silent = false } = {}) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const response = await axios.get(WAITINGLIST_ENDPOINT, {
        params: {
          limit: 250,
          q: query.trim() || undefined,
        },
      });

      setItems(Array.isArray(response.data?.items) ? response.data.items : []);
      setError("");
      setLastUpdated(new Date());
    } catch (fetchError) {
      console.error("Eroare la preluarea waitinglist:", fetchError);
      setError("Nu am putut incarca waitinglist-ul.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [query]);

  useEffect(() => {
    fetchWaitinglist();
  }, [fetchWaitinglist]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchWaitinglist({ silent: true });
    }, POLL_INTERVAL_MS);

    const handleFocus = () => {
      fetchWaitinglist({ silent: true });
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
    };
  }, [fetchWaitinglist]);

  const uniqueEmails = new Set(
    items.map((item) => String(item.contact_email || "").trim()).filter(Boolean)
  ).size;
  const uniqueSkus = new Set(
    items.map((item) => String(item.product_sku || "").trim()).filter(Boolean)
  ).size;
  const latestEntry = items[0]?.created_at
    ? dayjs(items[0].created_at).format("DD.MM.YYYY HH:mm")
    : "-";

  return (
    <Box sx={{ display: "grid", gap: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Waitinglist
          </Typography>
          <Typography variant="body1" sx={{ color: "#475569", mt: 0.75 }}>
            Cererile trimise din pagina produselor fara stoc apar aici automat.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {lastUpdated && (
            <Chip
              label={`Actualizat ${dayjs(lastUpdated).format("HH:mm:ss")}`}
              sx={{ background: "rgba(15,23,42,0.06)", color: "#334155" }}
            />
          )}
          <Button
            variant="contained"
            startIcon={
              refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
            }
            onClick={() => fetchWaitinglist({ silent: true })}
            disabled={refreshing}
            sx={{
              background: "#0f172a",
              "&:hover": { background: "#111827" },
            }}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        <Paper sx={{ p: 2.25, borderRadius: 3 }}>
          <Typography variant="overline" sx={{ color: "#64748b" }}>
            Total cereri
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {items.length}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2.25, borderRadius: 3 }}>
          <Typography variant="overline" sx={{ color: "#64748b" }}>
            Emailuri unice
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {uniqueEmails}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2.25, borderRadius: 3 }}>
          <Typography variant="overline" sx={{ color: "#64748b" }}>
            Ultima cerere
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {latestEntry}
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
            SKU-uri unice: {uniqueSkus}
          </Typography>
        </Paper>
      </Box>

      <Paper sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Lista cereri
          </Typography>
          <TextField
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Cauta dupa nume, telefon, email, SKU, produs, brand"
            size="small"
            sx={{ minWidth: { md: 420 } }}
          />
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: "70vh", borderRadius: 2 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Nume</TableCell>
                  <TableCell>Telefon</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Produs</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      Nu exista cereri in waitinglist.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {item.created_at
                          ? dayjs(item.created_at).format("DD.MM.YYYY HH:mm")
                          : "-"}
                      </TableCell>
                      <TableCell>{item.contact_name || "-"}</TableCell>
                      <TableCell>{item.contact_phone || "-"}</TableCell>
                      <TableCell>{item.contact_email || "-"}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {item.product_sku || "-"}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.product_name || "-"}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {item.product_brand ? (
                              <Chip size="small" label={item.product_brand} />
                            ) : null}
                            {item.product_permalink ? (
                              <Button
                                size="small"
                                href={item.product_permalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ minWidth: 0, px: 0, textTransform: "none" }}
                              >
                                Vezi produs
                              </Button>
                            ) : null}
                          </Stack>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default WaitingListPage;
